import { Icon } from "@meiden-connect/ui";
import type { useThemeMode } from "@meiden-connect/ui";
import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { DASHBOARD_THEME_MODE_ATTRIBUTE } from "../../components/dashboard-shell/dashboard-theme-mode-attribute";

export interface CanvasCardContextMenuItem {
  readonly key: string;
  readonly label: string;
  readonly iconData: string;
  readonly danger?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  readonly onSelect: () => void;
}

interface Props {
  readonly clientX: number;
  readonly clientY: number;
  readonly anchor: HTMLElement;
  readonly items: readonly CanvasCardContextMenuItem[];
  /** portal 先でも TOP と同じ配色トークンを解決するためのテーマモード。 */
  readonly themeMode: ReturnType<typeof useThemeMode>;
  readonly onClose: () => void;
}

const MENU_WIDTH = 220;
const VIEWPORT_GAP = 8;

// TOP のカード専用コンテキストメニュー。表示位置を画面内へ収め、
// キーボード操作とフォーカス復帰を含めて通常のメニューとして扱えるようにする。
export function CanvasCardContextMenu({
  clientX,
  clientY,
  anchor,
  items,
  themeMode,
  onClose,
}: Props): JSX.Element | null {
  const menuRef = useRef<HTMLUListElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number }>();

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const rect = menu.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const originX = clientX === 0 && clientY === 0 ? anchorRect.left : clientX;
    const originY = clientX === 0 && clientY === 0 ? anchorRect.bottom : clientY;
    setPosition({
      left: clamp(originX, VIEWPORT_GAP, window.innerWidth - rect.width - VIEWPORT_GAP),
      top: clamp(originY, VIEWPORT_GAP, window.innerHeight - rect.height - VIEWPORT_GAP),
    });
    menu.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [anchor, clientX, clientY]);

  useEffect(() => {
    const closeAndRestoreFocus = (): void => {
      onClose();
      restoreAnchorFocus(anchor);
    };
    const handlePointerDown = (event: PointerEvent): void => {
      if (!(event.target instanceof Node) || !menuRef.current?.contains(event.target)) {
        closeAndRestoreFocus();
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeAndRestoreFocus();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const buttons = Array.from(
        menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ??
          []
      );
      if (buttons.length === 0) return;
      event.preventDefault();
      const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const direction = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex = (currentIndex + direction + buttons.length) % buttons.length;
      buttons[nextIndex]?.focus();
    };
    window.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", closeAndRestoreFocus, true);
    window.addEventListener("resize", closeAndRestoreFocus);
    window.addEventListener("scroll", closeAndRestoreFocus, true);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", closeAndRestoreFocus, true);
      window.removeEventListener("resize", closeAndRestoreFocus);
      window.removeEventListener("scroll", closeAndRestoreFocus, true);
    };
  }, [anchor, onClose]);

  if (items.length === 0) return null;
  return createPortal(
    <ul
      ref={menuRef}
      role="menu"
      aria-label="キャンバス操作"
      data-testid="canvas-card-context-menu"
      {...{ [DASHBOARD_THEME_MODE_ATTRIBUTE]: themeMode }}
      style={{
        position: "fixed",
        left: `${position?.left ?? 0}px`,
        top: `${position?.top ?? 0}px`,
        visibility: position ? "visible" : "hidden",
        width: `${MENU_WIDTH}px`,
        padding: "4px",
        border: "1px solid var(--color-border, #d8dbe6)",
        borderRadius: "8px",
        background: "var(--color-surface, #ffffff)",
        color: "var(--color-text, #1a1a2e)",
        // 影はテーマトークンから解決する（ダークでは濃い影 + 薄い縁）。フォールバックは従来値。
        boxShadow: "var(--shadow-menu, 0 8px 24px rgba(0, 0, 0, 0.24))",
        zIndex: 1000,
        listStyle: "none",
      }}
    >
      {items.map((item) => (
        <li key={item.key}>
          {/* hover / focus-visible の背景は styles.css のクラスで与える。インラインに背景を
              残すと疑似クラス規則より優先されるため、通常時の透明指定も CSS 側へ移している。 */}
          <button
            type="button"
            role="menuitem"
            className="dashboard-card-context-menu__item"
            disabled={item.disabled}
            onClick={() => {
              item.onSelect();
              onClose();
              restoreAnchorFocus(anchor);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              padding: "8px 10px",
              border: "none",
              borderRadius: "6px",
              color: item.danger ? "var(--color-danger, #d22b2b)" : "var(--color-text, #1a1a2e)",
              cursor: item.disabled ? "default" : "pointer",
              opacity: item.disabled ? 0.5 : 1,
              textAlign: "left",
            }}
          >
            <Icon d={item.iconData} />
            <span>{item.label}</span>
          </button>
        </li>
      ))}
    </ul>,
    document.body
  );
}

function restoreAnchorFocus(anchor: HTMLElement): void {
  queueMicrotask(() => {
    if (anchor.isConnected) anchor.focus();
  });
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}
